import { useEffect, useState } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';

// Redirect component that preserves eventId query parameter
function NavigateToIncidents() {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const eventId = params.get('eventId');
  const target = eventId ? `/workspace/incidents?eventId=${eventId}` : '/workspace/incidents';
  return <Navigate to={target} replace />;
}
import { useAppSelector, useAppDispatch } from './store';
import { fetchCurrentUser } from './store/slices/authSlice';
import { setViewMode } from './store/slices/uiSlice';
import { canAccessCommand, isMDTOnly } from './lib/permissions';
import LoginPage from './pages/LoginPage';
import CommandHomePage from './pages/CommandHomePage';
import SplitOpsLayout from './layouts/SplitOpsLayout';
import MobileLayout from './layouts/MobileLayout';
import IncidentsPage from './pages/IncidentsPage';
import IncidentDetailPage from './pages/IncidentDetailPage';
import MajorIncidentDetailPage from './pages/MajorIncidentDetailPage';
import ResourcesPage from './pages/ResourcesPage';
import MobileDispatchPage from './pages/MobileDispatchPage';
import MobileMapPage from './pages/MobileMapPage';
import MobileIncidentPage from './pages/MobileIncidentPage';
import MdtResourceSelect from './pages/MdtResourceSelect';
import MdtEventSelect from './pages/MdtEventSelect';
import PRFPage from './pages/PRFPage';
import MinorInjuryPage from './pages/MinorInjuryPage';
import MdtFormsHubPage from './pages/MdtFormsHubPage';
import PRFListPage from './pages/PRFListPage';
import PRFDetailPage from './pages/PRFDetailPage';
import AdminPage from './pages/AdminPage';
import RoleManagementPage from './pages/RoleManagementPage';
import MapPage from './pages/MapPage';
import EventDisclosurePage from './pages/EventDisclosurePage';
import EventDisclosurePublicPage from './pages/EventDisclosurePublicPage';
import AdminShell from './layouts/AdminShell';
import { EventProvider } from './contexts/EventContext';
import WorkspaceErrorBoundary from './components/shared/WorkspaceErrorBoundary';
import { SystemConfigProvider } from './contexts/SystemConfigContext';
import PWAInstallPrompt from './components/shared/PWAInstallPrompt';
import OfflineIndicator from './components/shared/OfflineIndicator';
import ActionQueueProcessor from './components/shared/ActionQueueProcessor';
import EventDataSync from './components/shared/EventDataSync';
import MdtPermissionsGate from './components/mobile/MdtPermissionsGate';
import { syncManager } from './lib/syncManager';
import { isMdtAppTarget } from './lib/native';
import { isSessionActive, clearSessionActive, MDT_RESOURCE_KEY, reconcileMdtResourceWithEvent, isMdtSelectableEvent, getMdtEventId } from './lib/mdtSessionStorage';
import { startSession, stopSession } from './lib/mdtSession';
import { unlockDispatchAudio } from './lib/dispatchAlertSound';
import { resetMdtDispatchState } from './store/slices/dispatchSlice';
import { MdtNavigationProvider } from './contexts/MdtNavigationContext';
import type { Resource } from './types';
import TerminalLockScreen from './components/shared/TerminalLockScreen';

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = useAppSelector((state) => state.auth);
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

function AdminRoute({ children }: { children: React.ReactNode }) {
  const { user } = useAppSelector((state) => state.auth);
  if (!user || user.role !== 'command_admin') {
    return <Navigate to="/" replace />;
  }
  return <>{children}</>;
}

function CommandOnlyRoute({ children }: { children: React.ReactNode }) {
  const { user } = useAppSelector((state) => state.auth);
  if (!user || !canAccessCommand(user.role)) {
    return <Navigate to="/" replace />;
  }
  return <>{children}</>;
}


function TerminalLockOverlay() {
  const terminalLocked = useAppSelector((state) => state.ui.terminalLocked);
  if (!terminalLocked) return null;
  return <TerminalLockScreen />;
}

export default function App() {
  const dispatch = useAppDispatch();
  const { isAuthenticated, user, token } = useAppSelector((state) => state.auth);
  const viewMode = useAppSelector((state) => state.ui.viewMode);

  // MDT event selection state
  const [mdtEvent, setMdtEvent] = useState<any | null>(() => {
    try {
      const stored = localStorage.getItem('c2_mdt_event');
      const parsed = stored ? JSON.parse(stored) : null;
      if (parsed && !isMdtSelectableEvent(parsed)) {
        localStorage.removeItem('c2_mdt_event');
        return null;
      }
      return parsed;
    } catch { return null; }
  });

  // MDT resource selection state
  const [mdtResource, setMdtResource] = useState<Resource | null>(() => {
    try {
      const stored = localStorage.getItem('c2_mdt_resource');
      const resource = stored ? JSON.parse(stored) : null;
      const eventStored = localStorage.getItem('c2_mdt_event');
      const event = eventStored ? JSON.parse(eventStored) : null;
      if (reconcileMdtResourceWithEvent(event, resource)) return null;
      return resource;
    } catch { return null; }
  });

  // Drop stale session if unit was from a different event than selected
  useEffect(() => {
    if (!mdtEvent || !mdtResource) return;
    if (reconcileMdtResourceWithEvent(mdtEvent, mdtResource)) {
      setMdtResource(null);
      setMdtSessionReady(false);
      clearSessionActive();
    }
  }, [mdtEvent, mdtResource]);

  // MDT session ready (permissions gate passed or cold-start resume)
  const [mdtSessionReady, setMdtSessionReady] = useState(() => {
    try {
      return isSessionActive() && !!localStorage.getItem(MDT_RESOURCE_KEY);
    } catch {
      return false;
    }
  });

  // Clear event and resource selection on logout
  useEffect(() => {
    if (!isAuthenticated) {
      setMdtEvent(null);
      setMdtResource(null);
      setMdtSessionReady(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (isAuthenticated) {
      dispatch(fetchCurrentUser());
    }
  }, [isAuthenticated, dispatch]);

  // Initialize view mode - lock map page to desktop mode
  useEffect(() => {
    const isMapPage = window.location.pathname === '/workspace/map';
    if (isMapPage) {
      dispatch(setViewMode('desktop'));
    } else {
      dispatch(setViewMode(window.innerWidth >= 1024 ? 'desktop' : 'mobile'));
    }
  }, [dispatch]);

  // Initialize sync manager and download offline data
  useEffect(() => {
    const initOffline = async () => {
      try {
        await syncManager.initialize();
        
        // Download data for offline use when authenticated
        if (isAuthenticated && token && navigator.onLine) {
          await syncManager.downloadDataForOffline(token);
        }
      } catch (error) {
        console.error('Failed to initialize offline mode:', error);
      }
    };

    initOffline();
  }, [isAuthenticated, token]);

  useEffect(() => {
    const handleResize = () => {
      // Don't switch to mobile view if we're on the standalone map page
      const isMapPage = window.location.pathname === '/workspace/map';
      if (isMapPage) {
        dispatch(setViewMode('desktop'));
      } else {
        dispatch(setViewMode(window.innerWidth >= 1024 ? 'desktop' : 'mobile'));
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [dispatch]);


  const forceMDT = user ? (isMDTOnly(user.role) || viewMode === 'mobile' || isMdtAppTarget()) : false;
  const useDesktop = viewMode === 'desktop' && !forceMDT && !isMdtAppTarget();

  const handleResourceSelect = (r: Resource) => {
    clearSessionActive();
    setMdtSessionReady(false);
    localStorage.setItem(MDT_RESOURCE_KEY, JSON.stringify(r));
    setMdtResource(r);
  };

  const handleChangeUnit = async () => {
    if (token && mdtResource?.id && !mdtResource.id.startsWith('temp-')) {
      await stopSession(token, mdtResource.id, getMdtEventId(mdtResource) ?? undefined);
    } else {
      clearSessionActive();
    }
    dispatch(resetMdtDispatchState());
    setMdtSessionReady(false);
    localStorage.removeItem(MDT_RESOURCE_KEY);
    setMdtResource(null);
  };

  const handleResourceUpdate = (resource: Resource) => {
    localStorage.setItem(MDT_RESOURCE_KEY, JSON.stringify(resource));
    setMdtResource(resource);
  };

  const handlePermissionsComplete = async (_opts: { notifications: boolean; location: boolean }) => {
    await unlockDispatchAudio();
    if (user && token && mdtResource) {
      await startSession({ resource: mdtResource, user, token });
    }
    setMdtSessionReady(true);
  };

  return (
    <SystemConfigProvider>
    {!isMdtAppTarget() && <PWAInstallPrompt />}
    <OfflineIndicator />
    {isAuthenticated && <ActionQueueProcessor />}
    {isAuthenticated && useDesktop && <TerminalLockOverlay />}
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/disclosure/:token" element={<EventDisclosurePublicPage />} />

      {useDesktop ? (
        <>
          {/* Command Home — landing page after login */}
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <CommandHomePage />
              </ProtectedRoute>
            }
          />

          {/* Standalone admin — NOT inside an event workspace */}
          <Route
            path="/admin"
            element={
              <ProtectedRoute>
                <AdminRoute>
                  <AdminShell>
                    <AdminPage />
                  </AdminShell>
                </AdminRoute>
              </ProtectedRoute>
            }
          />

          {/* Role Management */}
          <Route
            path="/admin/roles"
            element={
              <ProtectedRoute>
                <AdminRoute>
                  <AdminShell>
                    <RoleManagementPage />
                  </AdminShell>
                </AdminRoute>
              </ProtectedRoute>
            }
          />

          {/* Patient Report Forms — standalone, not inside event workspace */}
          <Route
            path="/prfs"
            element={
              <ProtectedRoute>
                <PRFListPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/prfs/:id"
            element={
              <ProtectedRoute>
                <PRFDetailPage />
              </ProtectedRoute>
            }
          />

          {/* Desktop workspace — entered when user opens an event */}
          <Route
            path="/workspace/*"
            element={
              <ProtectedRoute>
                <EventProvider>
                  <EventDataSync />
                  <WorkspaceErrorBoundary label="workspace">
                  <Routes>
                    {/* Standalone Map Page (pop-out) */}
                    <Route path="/map" element={<MapPage />} />
                    
                    {/* Regular workspace routes */}
                    <Route path="/*" element={
                      <SplitOpsLayout>
                        <Routes>
                          <Route path="/" element={<NavigateToIncidents />} />
                          <Route path="/incidents" element={<IncidentsPage />} />
                          <Route path="/incidents/:id" element={<IncidentDetailPage />} />
                          <Route path="/major-incidents/:id" element={<MajorIncidentDetailPage />} />
                          <Route path="/resources" element={
                            <CommandOnlyRoute><ResourcesPage /></CommandOnlyRoute>
                          } />
                          <Route path="/disclosure" element={
                            <CommandOnlyRoute><EventDisclosurePage /></CommandOnlyRoute>
                          } />
                          <Route path="*" element={<Navigate to="/workspace/incidents" replace />} />
                        </Routes>
                      </SplitOpsLayout>
                    } />
                  </Routes>
                  </WorkspaceErrorBoundary>
                </EventProvider>
              </ProtectedRoute>
            }
          />

          {/* Catch-all redirects to command home */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </>
      ) : (
        <Route
          path="/*"
          element={
            <ProtectedRoute>
              {forceMDT && !mdtEvent ? (
                <MdtEventSelect onSelect={(e) => setMdtEvent(e)} />
              ) : forceMDT && !mdtResource ? (
                <MdtResourceSelect selectedEvent={mdtEvent} onSelect={handleResourceSelect} />
              ) : forceMDT && mdtResource && !mdtSessionReady ? (
                <MdtPermissionsGate resource={mdtResource} onComplete={handlePermissionsComplete} />
              ) : (
                <MdtNavigationProvider changeUnit={handleChangeUnit} updateResource={handleResourceUpdate}>
                  <MobileLayout mdtResource={mdtResource} sessionReady={mdtSessionReady}>
                    <Routes>
                      <Route path="/" element={<MobileDispatchPage />} />
                      <Route path="/map" element={<MobileMapPage />} />
                      <Route path="/incident" element={<MobileIncidentPage />} />
                      <Route path="/forms" element={<MdtFormsHubPage />} />
                      <Route path="/prf" element={<PRFPage />} />
                      <Route path="/minor-injury" element={<MinorInjuryPage />} />
                      <Route path="/incidents/:id" element={<IncidentDetailPage />} />
                      <Route path="*" element={<Navigate to="/" replace />} />
                    </Routes>
                  </MobileLayout>
                </MdtNavigationProvider>
              )}
            </ProtectedRoute>
          }
        />
      )}
    </Routes>
    </SystemConfigProvider>
  );
}
